import {
  buildCollectorUpdatePayload,
  collectorSections,
  prefillFromItem,
  todayIn,
  type CollectorCategory,
  type CollectorEditItem,
  type CollectorUpdatePayload,
  type CollectorValues,
} from "@kovernow/flows/add_item";
import { useEffect, useRef, useState } from "react";
import { useServices } from "../../core/app-context";
import { useT } from "../../core/use-t";
import { CollectorSectionStep, type CollectorFormSection } from "./CollectorSectionStep";
import type { ServerFieldErrors } from "./shared";

/**
 * A collector item's schema form in edit mode: prefilled from the stored
 * item (cents → dollars, `<key>Specify` → the specify input, `purchasedAt`
 * → the purchase date), without the documents section, ending in "Save
 * changes". `onSave` gets the `PUT /items` body: the stored attributes with
 * the edits merged on top, and the documents unchanged.
 *
 * The section can be controlled (`section` / `onSection`, the edit route's
 * `?step=`); otherwise it is local state.
 */
export function CollectorForm({
  category,
  editItem,
  onSave,
  section,
  onSection,
  serverErrors = {},
  clearServerError = () => undefined,
  values: controlledValues,
  setValues: setControlledValues,
}: {
  category: CollectorCategory;
  editItem: CollectorEditItem;
  onSave: (payload: CollectorUpdatePayload) => void;
  section?: CollectorFormSection;
  onSection?: (section: CollectorFormSection) => void;
  /** Server field errors from `PUT /items`, by field key (i18n keys). */
  serverErrors?: ServerFieldErrors;
  clearServerError?: (field: string) => void;
  /** Lifted form values (kept across `?step=` changes by the edit route). */
  values?: CollectorValues;
  setValues?: (update: (v: CollectorValues) => CollectorValues) => void;
}) {
  const t = useT();
  const { config, format } = useServices();
  const sections = collectorSections(category).filter((s): s is CollectorFormSection => s !== "documents");
  const [localIndex, setLocalIndex] = useState(0);
  const [localValues, setLocalValues] = useState<CollectorValues>(() => prefillFromItem(category, editItem));
  const values = controlledValues ?? localValues;
  const setValues = setControlledValues ?? setLocalValues;
  const controlledIndex = section ? sections.indexOf(section) : -1;
  const index = controlledIndex >= 0 ? controlledIndex : localIndex;
  const setIndex = (i: number) => (onSection ? onSection(sections[i]!) : setLocalIndex(i));
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [index]);

  const sectionId = sections[index]!;
  const last = index === sections.length - 1;
  const today = todayIn(config.timeZone);
  return (
    <div className="flex flex-col gap-4" data-testid="collector-edit">
      <p className="text-sm font-semibold text-muted-foreground" data-testid="step-of">
        {t("addItem.stepOf", { n: format.number(index + 1), total: format.number(sections.length) })}
      </p>
      <CollectorSectionStep
        key={sectionId}
        category={category}
        sectionId={sectionId}
        values={values}
        setValues={setValues}
        ctx={{ today, currency: config.currency }}
        headingRef={headingRef}
        serverErrors={serverErrors}
        clearServerError={clearServerError}
        onBack={index > 0 ? () => setIndex(index - 1) : undefined}
        onContinue={() =>
          last ? onSave(buildCollectorUpdatePayload(category, editItem, values, config.currency)) : setIndex(index + 1)
        }
        continueLabel={last ? t("addItem.saveChanges") : undefined}
        showSubmitNotices={last}
      />
    </div>
  );
}
